import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { $getRoot, COMMAND_PRIORITY_EDITOR } from "lexical";
import { useEffect, useRef, type FC } from "react";
import { useAnalytics } from "../../../../analytics/analytics";
import { useIsNewMoment } from "../../../../moment/component/moment-new-context";
import { useLexicalContext } from "../../lexical-context";
import { $createSurveyAnswersNode, $isSurveyAnswersNode } from "../../nodes/survey-answers/helpers";
import { INSERT_SURVEY_ANSWERS_COMMAND } from "../../types/commands";
import { $insertNode } from "../../utils/insert-node";

const SURVEY_RESPONSE_CHANNELS = ["work_email", "personal_email", "slack", "teams"];

const supportsSurveyResponseBlock = (channelName?: string | null): boolean =>
  SURVEY_RESPONSE_CHANNELS.includes(channelName ?? "");

export type SurveyAnswersReconciliation = "added" | "removed" | null;

export function $reconcileSurveyAnswers({
  eligible,
  becameEligible,
  surveyId,
  headless,
}: {
  eligible: boolean;
  becameEligible: boolean;
  surveyId: string;
  headless: boolean;
}): SurveyAnswersReconciliation {
  const root = $getRoot();
  const node = root.getChildren().find($isSurveyAnswersNode);
  if (!eligible) {
    if (!node) return null;
    node.remove();
    return "removed";
  }
  if (becameEligible && !node) {
    root.append($createSurveyAnswersNode(headless, surveyId));
    return "added";
  }
  if (node && node.getSurveyId() !== surveyId) node.setSurveyId(surveyId);
  return null;
}

export const SurveyAnswersPlugin: FC = () => {
  const [editor] = useLexicalComposerContext();
  const { triggerSurveyId, isSurveyTrigger, channelName } = useLexicalContext();
  const { gaEvent } = useAnalytics();
  const isNewMoment = useIsNewMoment();
  const eligible = isSurveyTrigger && supportsSurveyResponseBlock(channelName);
  const wasEligible = useRef(isNewMoment ? false : eligible);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          INSERT_SURVEY_ANSWERS_COMMAND,
          () => {
            const alreadyExists = $getRoot().getChildren().some($isSurveyAnswersNode);
            if (alreadyExists) return true;
            $insertNode($createSurveyAnswersNode(editor._headless, triggerSurveyId ?? ""));
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
      ),
    [editor, triggerSurveyId],
  );

  useEffect(() => {
    if (!eligible) {
      editor.update(() => {
        const result = $reconcileSurveyAnswers({
          eligible: false,
          becameEligible: false,
          surveyId: triggerSurveyId ?? "",
          headless: editor._headless,
        });
        if (result !== "removed") return;
        gaEvent("survey_response_block_removed", {
          action: supportsSurveyResponseBlock(channelName)
            ? "trigger_changed"
            : "channel_unsupported",
        });
      });
      wasEligible.current = false;
      return;
    }

    editor.update(() => {
      const result = $reconcileSurveyAnswers({
        eligible: true,
        becameEligible: !wasEligible.current,
        surveyId: triggerSurveyId ?? "",
        headless: editor._headless,
      });
      if (result === "added") {
        gaEvent("survey_response_block_added", { action: "automatic" });
      }
    });
    wasEligible.current = true;
  }, [channelName, editor, eligible, gaEvent, triggerSurveyId]);

  return null;
};
